import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { TrainingStateGraph } from "./TrainingStateGraph.component";
const HORIZONTAL_SPACING = 170;
const VERTICAL_SPACING = 130;
export const TrainingObjectTab = ({ scenario, debugVersion }) => {
    var _a, _b, _c, _d, _e;
    const objects = (_a = scenario === null || scenario === void 0 ? void 0 : scenario.objects) !== null && _a !== void 0 ? _a : [];
    const [selectedObjectId, setSelectedObjectId] = useState((_b = objects[0]) === null || _b === void 0 ? void 0 : _b.id);
    const eventConditionInfoById = useMemo(() => {
        var _a;
        const conditionMap = new Map();
        for (const conditionalEvent of (_a = scenario === null || scenario === void 0 ? void 0 : scenario.events) !== null && _a !== void 0 ? _a : []) {
            const values = Array.isArray(conditionalEvent.value) ? conditionalEvent.value : [conditionalEvent.value];
            const evaluation = conditionalEvent.condition.evaluateWithTrace(values[0].context);
            for (const event of values) {
                conditionMap.set(event.id, {
                    text: conditionalEvent.condition.conditionText,
                    traces: evaluation.traces
                });
            }
        }
        return conditionMap;
    }, [scenario, debugVersion]);
    useEffect(() => {
        var _a;
        if (objects.length === 0) {
            setSelectedObjectId(undefined);
            return;
        }
        if (selectedObjectId !== undefined && objects.some((object) => object.id === selectedObjectId))
            return;
        setSelectedObjectId((_a = objects[0]) === null || _a === void 0 ? void 0 : _a.id);
    }, [objects, selectedObjectId]);
    const selectedObject = useMemo(() => objects.find((object) => object.id === selectedObjectId), [objects, selectedObjectId]);
    const graph = useMemo(() => selectedObject === undefined ? undefined : buildObjectGraph(selectedObject, eventConditionInfoById), [selectedObject, eventConditionInfoById]);
    if (scenario === undefined) {
        return React.createElement("div", { className: "training-stage-empty" }, "No training scenario available yet.");
    }
    return (React.createElement("div", { className: "training-object-tab" },
        React.createElement("div", { className: "training-object-list" }, objects.map((object) => {
            const isSelected = object.id === selectedObjectId;
            return (React.createElement("button", { key: object.id, className: `training-object-list-item${isSelected ? " selected" : ""}`, onClick: () => {
                    setSelectedObjectId(object.id);
                } }, object.id));
        })),
        React.createElement("div", { className: "training-object-detail" }, selectedObject === undefined
            ? (React.createElement("div", { className: "training-stage-empty" }, "Select a training object."))
            : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "training-object-info" },
                    React.createElement("div", { className: "training-object-info-row" },
                        React.createElement("span", null, "ID"),
                        React.createElement("span", null, selectedObject.id)),
                    React.createElement("div", { className: "training-object-info-row" },
                        React.createElement("span", null, "modelUid"),
                        React.createElement("span", null, selectedObject.modelUid)),
                    React.createElement("div", { className: "training-object-info-row" },
                        React.createElement("span", null, "modelName"),
                        React.createElement("span", null, selectedObject.modelName)),
                    React.createElement("div", { className: "training-object-info-row" },
                        React.createElement("span", null, "position"),
                        React.createElement("span", null, formatPosition(selectedObject.position))),
                    React.createElement("div", { className: "training-object-info-row" },
                        React.createElement("span", null, "currentState"),
                        React.createElement("span", null, (_d = (_c = selectedObject.getCurrentState()) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : "-"))),
                React.createElement("div", { className: "training-object-graph-panel" }, graph === undefined || graph.nodes.length === 0
                    ? (React.createElement("div", { className: "training-stage-empty" }, "This object has no states."))
                    : (React.createElement(TrainingStateGraph, { graphKey: selectedObject.id, nodes: graph.nodes, edges: graph.edges, defaultPositions: graph.defaultPositions, currentNodeId: (_e = selectedObject.getCurrentState()) === null || _e === void 0 ? void 0 : _e.name, nodeRadius: 38, hintText: "Left drag: move state or label. Right drag: pan.", arrowMarkerId: "training-object-state-arrow" }))))))));
};
function buildObjectGraph(trainingObject, eventConditionInfoById) {
    const stateNodes = trainingObject.getAllStates();
    const edges = [];
    const adjacency = new Map();
    const transitionsByPair = new Map();
    stateNodes.forEach((stateNode) => {
        const nextStateIds = Array.from(new Set(stateNode.getNextStates().map((nextState) => nextState.data.name)));
        adjacency.set(stateNode.data.name, nextStateIds);
    });
    trainingObject.transitions.forEach((transition) => {
        var _a;
        const pairKey = `${transition.startState}->${transition.endState}`;
        const transitions = (_a = transitionsByPair.get(pairKey)) !== null && _a !== void 0 ? _a : [];
        transitions.push(transition);
        transitionsByPair.set(pairKey, transitions);
    });
    transitionsByPair.forEach((transitions, pairKey) => {
        transitions.forEach((transition, index) => {
            var _a, _b;
            const conditionInfo = eventConditionInfoById.get(transition.eventId);
            const label = (_a = conditionInfo === null || conditionInfo === void 0 ? void 0 : conditionInfo.text) !== null && _a !== void 0 ? _a : transition.eventId;
            edges.push({
                id: `${pairKey}-${transition.eventId}-${index}`,
                fromId: transition.startState,
                toId: transition.endState,
                labelSegments: buildConditionLabelSegments(label, (_b = conditionInfo === null || conditionInfo === void 0 ? void 0 : conditionInfo.traces) !== null && _b !== void 0 ? _b : []),
                groupIndex: index,
                groupCount: transitions.length,
                hasReverseEdge: transitionsByPair.has(`${transition.endState}->${transition.startState}`)
            });
        });
    });
    const levels = computeLevels(stateNodes, adjacency, (stateNode) => stateNode.data.name);
    const nodes = stateNodes.map((stateNode) => ({
        id: stateNode.data.name,
        label: stateNode.data.name,
        fill: "#b6b6b6"
    }));
    const nodesByLevel = new Map();
    nodes.forEach((node) => {
        var _a, _b;
        const level = (_a = levels.get(node.id)) !== null && _a !== void 0 ? _a : 0;
        const levelNodes = (_b = nodesByLevel.get(level)) !== null && _b !== void 0 ? _b : [];
        levelNodes.push(node);
        nodesByLevel.set(level, levelNodes);
    });
    const defaultPositions = {};
    Array
        .from(nodesByLevel.entries())
        .sort((a, b) => a[0] - b[0])
        .forEach(([level, levelNodes]) => {
        levelNodes
            .sort((a, b) => a.id.localeCompare(b.id))
            .forEach((node, index) => {
            defaultPositions[node.id] = {
                x: 110 + index * HORIZONTAL_SPACING,
                y: 95 + level * VERTICAL_SPACING
            };
        });
    });
    return { nodes, edges, defaultPositions };
}
function computeLevels(stateNodes, adjacency, getId) {
    var _a, _b;
    const levels = new Map();
    const root = stateNodes[0] !== undefined ? getId(stateNodes[0]) : undefined;
    if (root !== undefined) {
        const queue = [root];
        levels.set(root, 0);
        while (queue.length > 0) {
            const currentId = queue.shift();
            if (currentId === undefined)
                continue;
            const currentLevel = (_a = levels.get(currentId)) !== null && _a !== void 0 ? _a : 0;
            for (const nextId of (_b = adjacency.get(currentId)) !== null && _b !== void 0 ? _b : []) {
                if (levels.has(nextId))
                    continue;
                levels.set(nextId, currentLevel + 1);
                queue.push(nextId);
            }
        }
    }
    let fallbackLevel = (Math.max(0, ...levels.values()) || 0) + 1;
    stateNodes.forEach((stateNode) => {
        const id = getId(stateNode);
        if (levels.has(id))
            return;
        levels.set(id, fallbackLevel++);
    });
    return levels;
}
function buildConditionLabelSegments(label, traces) {
    const leafTraces = traces
        .filter((trace) => trace.kind === "leaf" || trace.kind === "literal")
        .sort((a, b) => a.start - b.start);
    if (leafTraces.length === 0) {
        return [{ text: label }];
    }
    const segments = [];
    let currentIndex = 0;
    for (const trace of leafTraces) {
        const start = clamp(trace.start, 0, label.length);
        const end = clamp(trace.end, 0, label.length);
        if (end <= start)
            continue;
        if (start > currentIndex) {
            segments.push({ text: label.slice(currentIndex, start) });
        }
        segments.push({
            text: label.slice(start, end),
            color: trace.result ? "#1a8f3e" : "#c43b3b"
        });
        currentIndex = end;
    }
    if (currentIndex < label.length) {
        segments.push({ text: label.slice(currentIndex) });
    }
    return segments.filter((segment) => segment.text.length > 0);
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function formatPosition(position) {
    return `${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`;
}
